import * as React from 'react';

import { EtaPicker, EtaDisplay } from '../../components/common/EtaPicker';
import { fmtDateTime } from '../utils/campaignDetailHelpers';

/** Which deliverable this ETA governs. Live has no review phase — going
 *  live is a single admin step, no revision loop to extend into. */
export type EtaTimelinePhase = 'script' | 'content' | 'live';

const PENDING_STATUS: Record<EtaTimelinePhase, string> = {
  script: 'script_pending',
  content: 'content_pending',
  live: 'live_pending',
};
const REVIEW_STATUS: Partial<Record<EtaTimelinePhase, string>> = {
  script: 'script_review',
  content: 'content_review',
};

// The review-state upload date doubling as the "set the next revision's ETA"
// trigger: plain text at rest — no box, so it reads as a timestamp, not a
// control. On hover/focus it gets a bordered, shadowed fill (like a pressed
// button) plus the accent text color, so it's unambiguous that only the
// stamp itself is clickable, not the row.
const DATE_TRIGGER =
  'border border-transparent outline-none hover:border-border hover:bg-muted hover:text-(--brand-600) hover:shadow-xs focus-visible:border-border focus-visible:bg-muted focus-visible:text-(--brand-600) focus-visible:shadow-xs focus-visible:ring-2 focus-visible:ring-ring -mx-2 -my-1 rounded-md px-2 py-1 text-left text-body-sm-medium text-foreground transition-all';

export interface EtaTimelineProps {
  /** The row's production status (e.g. 'content_pending' / 'script_review'). */
  status: string;
  phase: EtaTimelinePhase;
  /** The row's ETA for this phase (ISO-8601 UTC), or null when unset.
   *  While pending this is the original delivery/go-live estimate; while
   *  in review (script/content only) it's the estimate for the NEXT
   *  revision — a re-upload while already in review doesn't change status
   *  (BE's Post.revise()), so it isn't the same estimate as the pending case. */
  eta: string | null;
  /** This phase's own dedicated "last touched" timestamp (ISO-8601 UTC) —
   *  script_updated_at / content_updated_at / went_live_at. Deliberately NOT
   *  the row's generic status_updated_at, which every phase's transitions
   *  share. Only ever shown when there's no ETA taking priority (see below). */
  updatedAt?: string;
  /** Admin may edit the ETA (permission + wired handler). Brand: false. */
  canEditEta: boolean;
  /** Persist a new ETA for this row/phase (null clears it). */
  onSetEta: (iso: string | null) => Promise<void> | void;
}

/**
 * Owns the whole Updated column: this phase's own timestamp, or its ETA,
 * whichever is more relevant right now — never both at once.
 *
 * - Pending: the ETA slot only (trigger, value, or blank for brand-unset) —
 *   nothing has been uploaded yet, so there's no "last touched" date to show.
 * - Review, no forward ETA set yet: `updatedAt` (when the current version
 *   landed) doubles as admin's entry point to set one — no separate button.
 *   Legacy rows with no `updatedAt` fall back to the bare "Set ETA" trigger
 *   for admin, and render nothing for brand.
 * - Review, ETA set: the ETA only — the date is superseded by "here's when
 *   the next one is expected."
 * - Anything else (phase already behind this row, or — for live — already
 *   published): `updatedAt` only, no ETA control; nothing at all if there's
 *   no date either.
 */
export function EtaTimeline({
  status,
  phase,
  eta,
  updatedAt,
  canEditEta,
  onSetEta,
}: EtaTimelineProps): React.ReactElement | null {
  const reviewStatus = REVIEW_STATUS[phase];
  const isReview = reviewStatus != null && status === reviewStatus;
  const isPending = status === PENDING_STATUS[phase];

  if (!isReview && !isPending) {
    if (!updatedAt) return null;
    return <div className="text-body-sm-medium text-foreground">{fmtDateTime(updatedAt)}</div>;
  }

  if (isPending) {
    if (!canEditEta && !eta) return null;
    return canEditEta ? (
      <EtaPicker value={eta} onChange={onSetEta} />
    ) : (
      <EtaDisplay iso={eta as string} />
    );
  }

  // Review, with a forward ETA already set — that supersedes the date.
  if (eta) {
    return canEditEta ? <EtaPicker value={eta} onChange={onSetEta} /> : <EtaDisplay iso={eta} />;
  }

  // Review, no forward ETA yet. Brand reads the plain upload date (or nothing,
  // on a legacy row with none). Admin gets the same date as the entry point to
  // set one — no separate trigger — falling back to the bare "Set ETA" pill
  // when there's no date to click.
  if (!canEditEta) {
    if (!updatedAt) return null;
    return <div className="text-body-sm-medium text-foreground">{fmtDateTime(updatedAt)}</div>;
  }
  if (!updatedAt) return <EtaPicker value={null} onChange={onSetEta} />;
  return (
    <EtaPicker
      value={null}
      onChange={onSetEta}
      trigger={
        <button type="button" aria-label="Edit ETA" title="Tap to edit" className={DATE_TRIGGER}>
          {fmtDateTime(updatedAt)}
        </button>
      }
    />
  );
}
